import Grid from '@mui/material/Grid2';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Box, IconButton, Tooltip, Menu, MenuItem, Chip, Alert, AlertTitle } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FallbackTypography from 'src/components/shared/FallbackTypography';
import dayjs from 'dayjs';
import { dateFormat } from 'src/utils/dateFormat';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { getReservationStatusChipColor, ReservationStatus } from 'src/utils/common';
import { cancelReservation } from 'src/store/reservations/slices/ReservationsManagerSlice';
import { useMsal } from '@azure/msal-react';
import { providerHelpers } from 'src/utils/providerConfig';
import CopyableText from 'src/components/common/CopyableText';
import BookingCancelConfirmationDialog from 'src/components/common/BookingCancelConfirmationDialog';

const ReservationInfo = () => {
    const { reservationOverview } = useSelector((state) => state.reservationsSummaryReducer);
    const [anchorEl, setAnchorEl] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [success, setSuccess] = useState(false);
    const [cancellationResults, setCancellationResult] = useState(null);
    const [error, setError] = useState(false);
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const { instance, accounts } = useMsal();

    const menuOpen = Boolean(anchorEl);

    const isRequiredCancelReservation = providerHelpers.requiresCancellation(reservationOverview?.Provider);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleCancelBookingClick = () => {
        handleMenuClose();
        setDialogOpen(true);
    };

    const handleConfirmCancel = async (note) => {
        setLoading(true);
        setSuccess(false);
        setError(false);

        try {

            const requestBody = {
                Provider: reservationOverview.Provider,
                CancellationNote: note
            }
            const result = await dispatch(cancelReservation(instance, accounts, reservationOverview.Id, requestBody));
            setCancellationResult(result);
            setSuccess(true);
            setLoading(false);

        } catch (err) {
            setError(err);
            setLoading(false);
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSuccess(false);
        setError(false);
        setLoading(false);
    };

    const handleRedirection = () => {
        handleDialogClose();
    };

    return (
        <Grid container sx={{ mb: 4 }}>
            <Grid container size={{ xs: 12, sm: 6 }} direction="column" alignItems="flex-start" spacing={2}>
                <Grid container size={12} direction="column" alignItems="flex-start" spacing={2} mb={1}>
                    <Typography variant="body1" fontWeight="bold">Booking Information</Typography>
                </Grid>

                <Grid container direction="column" item spacing={2}>
                    <Grid item>
                        <Typography variant="body1" component="span">Booking Ref: </Typography>
                        <CopyableText value={reservationOverview?.ReservationReference} />
                    </Grid>

                    <Grid item>
                        <Typography variant="body1" component="span">Booking No: </Typography>
                        <CopyableText value={reservationOverview?.ReservationNumber} />
                    </Grid>

                    <Grid item>
                        <Typography variant="body1" component="span">Status: </Typography>
                        {reservationOverview?.Status === ReservationStatus.Cancelled && reservationOverview.CancellationNote ? (
                            <Tooltip
                                title={
                                    <Box sx={{ flex: 1 }}>
                                        <Alert variant="outlined" severity="error">
                                            <AlertTitle>Cancellation Note</AlertTitle>
                                            <strong>{reservationOverview.CancellationNote}</strong>
                                        </Alert>
                                    </Box>
                                }
                                arrow
                                placement="right"
                                slotProps={{
                                    tooltip: {
                                        sx: {
                                            backgroundColor: 'transparent',
                                            padding: 0,
                                            maxWidth: 600,
                                        }
                                    }
                                }}
                            >
                                <Chip
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <span>{reservationOverview?.Status}</span>
                                            <VisibilityIcon sx={{ fontSize: '0.875rem' }} />
                                        </Box>
                                    }
                                    size="small"
                                    color={getReservationStatusChipColor(reservationOverview?.Status)}
                                    sx={{
                                        fontSize: "0.75rem",
                                        fontWeight: "bold",
                                        cursor: 'pointer',
                                        '&:hover': {
                                            opacity: 0.9,
                                        }
                                    }}
                                />
                            </Tooltip>
                        ) : (
                            <Chip
                                label={reservationOverview?.Status}
                                size="small"
                                color={getReservationStatusChipColor(reservationOverview?.Status)}
                                sx={{ fontSize: "0.75rem", fontWeight: "bold" }}
                            />
                        )}
                    </Grid>

                    <Grid item>
                        <Typography variant="body1" component="span">Date: </Typography>
                        <FallbackTypography
                            variant="body1"
                            fontWeight="bold"
                            component="span"
                            value={reservationOverview?.Timestamp && dayjs(reservationOverview.Timestamp, dateFormat).format(dateFormat)}
                        />
                    </Grid>
                </Grid>
            </Grid>


            <Grid size={{ xs: 12, sm: 6 }} container direction="column" alignItems="flex-end">

                <Grid container size={12} direction="column" alignItems="flex-end" spacing={2} mb={3}>
                    <>
                        {/* Show menu button only when reservation not cancelled */}
                        {isRequiredCancelReservation && reservationOverview?.Status !== ReservationStatus.Cancelled && (
                            <>
                                <IconButton
                                    onClick={handleMenuOpen}
                                    aria-label="more options"
                                    aria-controls={menuOpen ? 'booking-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={menuOpen ? 'true' : undefined}
                                >
                                    <MoreVertIcon />
                                </IconButton>

                                <Menu
                                    id="booking-menu"
                                    anchorEl={anchorEl}
                                    open={menuOpen}
                                    onClose={handleMenuClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                >
                                    <MenuItem
                                        onClick={handleCancelBookingClick}
                                        sx={{
                                            backgroundColor: '#f2f6ff',
                                            '&:hover': {
                                                backgroundColor: 'primary.main',
                                                color: 'primary.contrastText',
                                            },
                                        }}
                                    >
                                        Cancel Reservation
                                    </MenuItem>
                                </Menu>
                            </>
                        )}

                        <BookingCancelConfirmationDialog   
                            open={dialogOpen}
                            success={success}
                            error={error}
                            loading={loading}
                            handleClose={handleDialogClose}
                            handleRedirection={handleRedirection}
                            handleConfirmCancel={handleConfirmCancel}
                            reservationOverview={reservationOverview}
                            cancellationResults={cancellationResults}
                        />
                    </>
                </Grid>

                <Grid container spacing={1} alignItems="center" justifyContent="end" mb={1}>
                    <Grid item>
                        <Typography variant="body1" component="span">Total Booking Cost:</Typography>
                    </Grid>
                    <Grid item>
                        <FallbackTypography
                            variant="body1"
                            fontWeight="bold"
                            component="span"
                            value={`${reservationOverview?.ClientCurrency} ${reservationOverview?.ClientNetSell?.Total}`}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={1} alignItems="center" justifyContent="end" mb={1}>
                    <Grid item>
                        <Typography variant="body1" component="span">Passenger Details:</Typography>
                    </Grid>
                    <Grid item>
                        <FallbackTypography
                            variant="body1"
                            fontWeight="bold"
                            component="span"
                            value={`${reservationOverview?.PaxCount} Pax`}
                        />
                    </Grid>
                </Grid>
            </Grid>

        </Grid>
    );
};

export default ReservationInfo;